import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { CalendarCheck, CreditCard, Loader2 } from "lucide-react";
import { BillingTerms } from "@/components/onboarding/BillingTerms";

export function CalendarStep({ connected, onNext }) {
  const [busy, setBusy] = useState(false);
  const connect = async () => {
    setBusy(true);
    try {
      const r = await api.get("/oauth/google/calendar/authorize");
      window.location.href = r.data.authorization_url;
    } catch (e) {
      setBusy(false);
      toast.error(e?.response?.data?.detail || "Could not start Google sign-in");
    }
  };
  return (
    <div className="space-y-5" data-testid="onboarding-step-calendar">
      <p className="text-sm text-slate-400">RoofLead reads your free/busy times and adds booked estimates to your Google Calendar.</p>
      {connected ? (
        <p className="text-emerald-300 flex items-center gap-2" data-testid="onboarding-calendar-connected"><CalendarCheck className="w-5 h-5" /> Google Calendar connected</p>
      ) : (
        <Button onClick={connect} disabled={busy} data-testid="onboarding-calendar-connect" className="bg-white text-slate-900 hover:bg-slate-200 rounded-full px-6">
          {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Connect Google Calendar
        </Button>
      )}
      <p className="text-xs text-slate-500">Pick the calendar and set working hours any time in <Link to="/app/settings" className="text-orange-400 underline">Settings</Link>.</p>
      <Button variant="ghost" onClick={onNext} data-testid="onboarding-calendar-next" className="text-slate-300">Continue to billing →</Button>
    </div>
  );
}

export function BillingStep({ billing, done, canceled }) {
  const [accept, setAccept] = useState(false);
  const [busy, setBusy] = useState(false);
  const start = async () => {
    setBusy(true);
    try {
      const r = await api.post("/billing/checkout", { origin_url: window.location.origin, accept_terms: accept });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      setBusy(false);
      toast.error(e?.response?.data?.detail || "Could not start checkout");
    }
  };
  if (done) return <p className="text-emerald-300" data-testid="onboarding-billing-done">Payment Successful — Pending Activation. <Link to="/app/billing" className="underline">View billing</Link></p>;
  return (
    <div className="space-y-5" data-testid="onboarding-step-billing">
      {canceled && <p className="text-amber-300 text-sm" data-testid="onboarding-billing-canceled">Checkout was canceled. Nothing was charged.</p>}
      <BillingTerms billing={billing} />
      <label className="flex gap-3 items-start">
        <Checkbox checked={accept} onCheckedChange={(c) => setAccept(!!c)} data-testid="onboarding-accept-terms" className="mt-0.5" />
        <span className="text-sm text-slate-300">I agree to pay ${billing?.initial_payment?.toLocaleString()} today (${billing?.setup_fee} setup + ${billing?.monthly_price} first month), then ${billing?.monthly_price}/month starting one month after my account is activated.</span>
      </label>
      <Button onClick={start} disabled={!accept || busy} data-testid="onboarding-start-checkout" className="bg-orange-500 hover:bg-orange-600 text-slate-900 font-semibold rounded-full px-6">
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />} Pay ${billing?.initial_payment?.toLocaleString()}
      </Button>
    </div>
  );
}
